import React, { type ReactElement, useMemo } from "react";

import { useAccountDetailState } from "@stores/account_detail";
import { range } from "@utilities/helper";
import { AnimatePresence, motion } from "motion/react";

interface Bubble {
  id: string;
  width: string;
  opacity: number;
  top: string;
  left: string;
}
const ProfileBubbleSection = (): ReactElement => {
  const accountDetailState = useAccountDetailState();
  const getFallbackPrefixName = useMemo((): string => {
    const name = accountDetailState.license.name;
    if (!name) {
      return "";
    }
    const parts = name.split(/\s+/);
    const initials = parts
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join("");

    return initials;
  }, [accountDetailState.license.name]);

  const bubbles = useMemo((): Bubble[] => {
    const minBubbleSize = 60;
    const maxBubbleSize = 80;

    const minBubbleOpacity = 0.1;
    const maxBubbleOpacity = 0.5;

    return range(3, 6).map(i => ({
      id: `bubble-${i}`,
      width: `${
        Math.random() * (maxBubbleSize - minBubbleSize) + minBubbleSize
      }px`,
      opacity:
        Math.random() * (maxBubbleOpacity - minBubbleOpacity) +
        minBubbleOpacity,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
    }));
  }, []);

  return (
    <div className="pointer-events-none relative flex flex-col pt-10">
      <div className="absolute left-1/2 top-0 z-10 size-20 -translate-x-1/2 overflow-hidden rounded-full border-4 border-white">
        {accountDetailState.profile.image ? (
          <img src={accountDetailState.profile.image} className="size-full" />
        ) : (
          <div className="flex size-full items-center justify-center bg-primary-100">
            <span className="text-lg font-semibold text-primary">
              {getFallbackPrefixName}
            </span>
          </div>
        )}
      </div>
      <div className="relative flex w-full flex-col items-center justify-center overflow-hidden rounded-md bg-secondary p-4 pt-14">
        <span className="pointer-events-auto text-lg font-semibold text-white">
          {accountDetailState.license.name}
        </span>
        <span className="pointer-events-auto text-base font-medium text-white">
          {accountDetailState.license.cli}
        </span>
        <AnimatePresence>
          {bubbles.map((bubble: Bubble) => (
            <motion.div
              key={bubble.id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: bubble.opacity.toFixed(2),
                scale: 1,
              }}
              exit={{ opacity: 0, scale: 0 }}
              style={{
                top: bubble.top,
                left: bubble.left,
                width: bubble.width,
              }}
              transition={{ duration: 0.3 }}
              className="pointer-events-none absolute aspect-square rounded-full bg-white"
            ></motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProfileBubbleSection;
